import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Plus, Trash2, Cloud } from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { getCashflowDetailed, listNamedScenarios, createNamedScenario, deleteNamedScenario } from '../../services/financialPlanningService';

const BRAND_GREEN = '#064e3b';

const formatCurrency = (v, currency = 'BRL') => {
  if (v == null || v === 0) return '—';
  const neg = v < 0;
  const abs = Math.abs(v);
  if (currency === 'USD') {
    return `${neg ? '-' : ''}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `${neg ? '-' : ''}R$${abs.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const formatMonth = (m) => {
  const [y, mo] = m.split('-');
  const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${months[parseInt(mo)-1]}/${y.slice(2)}`;
};

const AccountRow = ({ account, months, viewMode, currency = 'BRL' }) => {
  const isGroup = account.is_group;
  const isTopLevel = !account.parent_code;
  const indent = account.parent_code ? (account.code.replace('FC.','').split('.').length - 1) * 16 : 0;
  const isAzure = !!account.is_azure;
  const creditCovered = !!account.azure_credit_covered;

  const rowClass = isTopLevel && isGroup
    ? 'bg-gray-100 font-bold border-t-2 border-gray-300'
    : isGroup
    ? 'bg-gray-50 font-semibold'
    : 'hover:bg-blue-50/30';

  const textColor = account.nature === 'debit' ? 'text-red-700' : 'text-green-800';

  return (
    <tr className={`border-b ${rowClass}`}>
      <td className="px-3 py-1.5 whitespace-nowrap sticky left-0 bg-inherit" style={{ paddingLeft: `${12 + indent}px` }}>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-400 font-mono w-12">{account.code}</span>
          <span className={`text-xs ${isGroup ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
            {account.name}
          </span>
          {isAzure && creditCovered && (
            <span className="flex items-center gap-0.5 text-[9px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded ml-1" title="Azure coberto por crédito">
              <Cloud size={8} /> crédito
            </span>
          )}
        </div>
      </td>
      {months.map(month => {
        const vals = account.values[month] || { real: 0, projected: 0 };
        const usdBrl = vals.usd_brl || 5.70;
        const realBrl = vals.real || 0;
        const projBrl = vals.projected || 0;
        const realDisp = currency === 'USD' ? realBrl / usdBrl : realBrl;
        const projDisp = currency === 'USD' ? projBrl / usdBrl : projBrl;
        // azure_memo: cost covered by credit (not in cash)
        const memoUsd = vals.azure_memo_usd || 0;
        const memoBrl = vals.azure_memo_brl || 0;
        const memoDisp = currency === 'USD' ? memoUsd : memoBrl;
        const showVal = viewMode === 'real' ? realDisp : projDisp;
        return (
          <td key={month} className={`px-2 py-1.5 text-right font-mono text-xs ${textColor}`}>
            {viewMode === 'both' ? (
              <div>
                <div className="text-gray-900">{formatCurrency(projDisp, currency)}</div>
                {realBrl !== 0 && <div className="text-[10px] text-blue-600">{formatCurrency(realDisp, currency)}</div>}
                {memoDisp > 0 && creditCovered && (
                  <div className="text-[9px] text-blue-400" title="Coberto por crédito Azure">
                    ☁ {formatCurrency(memoDisp, currency)}
                  </div>
                )}
              </div>
            ) : (
              formatCurrency(showVal, currency)
            )}
          </td>
        );
      })}
    </tr>
  );
};

const FinancialCashflowDetailedTab = () => {
  const { lang } = useGovernanceLang();
  const L = {
    pt: {
      title: 'Fluxo de Caixa Detalhado',
      both: 'Real + Projetado', projected: 'Só Projetado', real: 'Só Realizado',
      base: 'Base', optimistic: 'Otimista', pessimistic: 'Pessimista',
      mo6: '6 meses', mo12: '12 meses', mo24: '24 meses', mo36: '36 meses',
      monthly: 'Mensal', quarterly: 'Trimestral', semiannual: 'Semestral', annual: 'Anual',
      confirmDelete: 'Excluir cenário?',
      scenarioPlaceholder: 'Nome do cenário (ex: Investimento 250k)',
      create: 'Criar',
      noData: 'Nenhum movimento de caixa encontrado. Adicione receitas e despesas com datas de pagamento/vencimento.',
      footer: 'Fluxo de caixa = Entradas - Saídas operacionais ± Investimentos. Edite premissas e gere projeção para dados completos.',
    },
    en: {
      title: 'Detailed Cash Flow',
      both: 'Actual + Projected', projected: 'Projected Only', real: 'Actual Only',
      base: 'Base', optimistic: 'Optimistic', pessimistic: 'Pessimistic',
      mo6: '6 months', mo12: '12 months', mo24: '24 months', mo36: '36 months',
      monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Semi-annual', annual: 'Annual',
      confirmDelete: 'Delete scenario?',
      scenarioPlaceholder: 'Scenario name (e.g. Investment 250k)',
      create: 'Create',
      noData: 'No cash flow entries found. Add revenues and expenses with payment/due dates.',
      footer: 'Cash flow = Inflows − Operating outflows ± Investments. Edit assumptions and generate projection for complete data.',
    },
  }[lang] || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [scenario, setScenario] = useState('base');
  const [viewMode, setViewMode] = useState('both');
  const [periodView, setPeriodView] = useState('monthly');
  const [scenarios, setScenarios] = useState([]);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [currency, setCurrency] = useState(
    () => localStorage.getItem('finops_currency') || 'BRL'
  );
  const handleCurrencyToggle = (c) => { setCurrency(c); localStorage.setItem('finops_currency', c); };

  const loadScenarios = useCallback(async () => {
    try {
      const res = await listNamedScenarios();
      setScenarios(res?.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCashflowDetailed(months, scenario, true);
      setData(res?.data || null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [months, scenario]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);
  useEffect(() => { load(); }, [load]);

  const handleCreateScenario = async () => {
    if (!newScenarioName.trim()) return;
    const key = newScenarioName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    await createNamedScenario({ name: newScenarioName.trim(), scenario_key: key });
    setNewScenarioName('');
    setShowNewForm(false);
    loadScenarios();
    setScenario(key);
  };

  const handleDeleteScenario = async (id, key) => {
    if (!window.confirm(L.confirmDelete)) return;
    await deleteNamedScenario(id);
    loadScenarios();
    if (scenario === key) setScenario('base');
  };

  if (loading) return <div className="h-96 bg-white rounded-xl animate-pulse" />;
  if (!data || !data.accounts) {
    return <div className="text-gray-500 text-center py-12">{L.noData}</div>;
  }

  const monthList = data.months || [];

  const aggregatePeriods = (monthsArr) => {
    if (periodView === 'monthly') return monthsArr.map(m => ({ key: m, label: formatMonth(m), months: [m] }));
    const periods = [];
    if (periodView === 'quarterly') {
      for (let i = 0; i < monthsArr.length; i += 3) {
        const slice = monthsArr.slice(i, i + 3);
        periods.push({ key: `Q${Math.floor(i/3)+1}`, label: `T${Math.floor(i/3)+1} ${slice[0]?.split('-')[0]}`, months: slice });
      }
    } else if (periodView === 'semiannual') {
      for (let i = 0; i < monthsArr.length; i += 6) {
        const slice = monthsArr.slice(i, i + 6);
        periods.push({ key: `S${Math.floor(i/6)+1}`, label: `S${Math.floor(i/6)+1} ${slice[0]?.split('-')[0]}`, months: slice });
      }
    } else if (periodView === 'annual') {
      const byYear = {};
      monthsArr.forEach(m => { const y = m.split('-')[0]; if (!byYear[y]) byYear[y] = []; byYear[y].push(m); });
      Object.entries(byYear).forEach(([y, ms]) => periods.push({ key: y, label: y, months: ms }));
    }
    return periods;
  };

  const periods = aggregatePeriods(monthList);
  const azureCredit = data.azure_credit_status || null;
  const aggregatedAccounts = data.accounts.map(acc => ({
    ...acc,
    is_azure: acc.is_azure,
    azure_credit_covered: acc.azure_credit_covered,
    values: Object.fromEntries(periods.map(p => [p.key, {
      real:            p.months.reduce((s, m) => s + (acc.values[m]?.real || 0), 0),
      projected:       p.months.reduce((s, m) => s + (acc.values[m]?.projected || 0), 0),
      azure_memo_brl:  p.months.reduce((s, m) => s + (acc.values[m]?.azure_memo_brl || 0), 0),
      azure_memo_usd:  p.months.reduce((s, m) => s + (acc.values[m]?.azure_memo_usd || 0), 0),
      usd_brl:         p.months.length > 0 ? (acc.values[p.months[p.months.length - 1]]?.azure_usd_brl || 5.70) : 5.70,
    }]))
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet size={18} style={{ color: BRAND_GREEN }} />
          <h2 className="text-lg font-bold text-gray-900">{L.title}</h2>
        </div>
        <div className="flex items-center gap-3">
          {/* Currency toggle */}
          <div className="flex items-center border rounded overflow-hidden text-xs">
            <button
              onClick={() => handleCurrencyToggle('BRL')}
              className={`px-2 py-1 font-bold transition-colors ${currency === 'BRL' ? 'bg-emerald-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >R$</button>
            <button
              onClick={() => handleCurrencyToggle('USD')}
              className={`px-2 py-1 font-bold transition-colors ${currency === 'USD' ? 'bg-blue-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >US$</button>
          </div>
          <select value={viewMode} onChange={e => setViewMode(e.target.value)} className="text-xs border rounded px-2 py-1">
            <option value="both">{L.both}</option>
            <option value="projected">{L.projected}</option>
            <option value="real">{L.real}</option>
          </select>
          <select value={scenario} onChange={e => setScenario(e.target.value)} className="text-xs border rounded px-2 py-1 font-semibold">
            <option value="base">{L.base}</option>
            <option value="optimistic">{L.optimistic}</option>
            <option value="pessimistic">{L.pessimistic}</option>
            {scenarios.filter(s => !['base','optimistic','pessimistic'].includes(s.scenario_key)).map(s => (
              <option key={s.id} value={s.scenario_key}>{s.name}</option>
            ))}
          </select>
          <button onClick={() => setShowNewForm(!showNewForm)} className="text-xs px-2 py-1 border rounded hover:bg-gray-100">
            <Plus size={12} />
          </button>
          <select value={months} onChange={e => setMonths(parseInt(e.target.value))} className="text-xs border rounded px-2 py-1">
            <option value={6}>{L.mo6}</option>
            <option value={12}>{L.mo12}</option>
            <option value={24}>{L.mo24}</option>
            <option value={36}>{L.mo36}</option>
          </select>
          <select value={periodView} onChange={e => setPeriodView(e.target.value)} className="text-xs border rounded px-2 py-1">
            <option value="monthly">{L.monthly}</option>
            <option value="quarterly">{L.quarterly}</option>
            <option value="semiannual">{L.semiannual}</option>
            <option value="annual">{L.annual}</option>
          </select>
        </div>
      </div>

      {showNewForm && (
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <input value={newScenarioName} onChange={e => setNewScenarioName(e.target.value)}
            placeholder={L.scenarioPlaceholder}
            className="flex-1 text-sm border rounded px-3 py-1.5"
            onKeyDown={e => e.key === 'Enter' && handleCreateScenario()} />
          <button onClick={handleCreateScenario} className="text-xs px-3 py-1.5 rounded text-white font-bold" style={{ backgroundColor: BRAND_GREEN }}>
            {L.create}
          </button>
          {scenarios.filter(s => !['base','optimistic','pessimistic'].includes(s.scenario_key)).map(s => (
            <span key={s.id} className="text-[10px] bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
              {s.name}
              <button onClick={() => handleDeleteScenario(s.id, s.scenario_key)} className="text-red-500 hover:text-red-700"><Trash2 size={10} /></button>
            </span>
          ))}
        </div>
      )}

      {viewMode === 'both' && (
        <div className="flex items-center gap-4 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-900 rounded-full" /> Projetado</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-600 rounded-full" /> Realizado</span>
          <span className="flex items-center gap-1"><span className="text-blue-400">☁</span> Azure (crédito – não sai do caixa)</span>
        </div>
      )}

      {/* Azure Credit Status Banner */}
      {azureCredit && !azureCredit._fallback && (
        <div className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm border ${
          azureCredit.is_exhausted
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          <Cloud size={16} />
          <div className="flex-1">
            <span className="font-bold">
              {azureCredit.is_exhausted ? '⚠️ Crédito Azure esgotado' : '☁ Crédito Azure ativo'}
            </span>
            {' '}— Saldo: <strong>
              ${(azureCredit.remaining_usd || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </strong> de ${(azureCredit.total_usd || 250000).toLocaleString('en-US', { maximumFractionDigits: 0 })} USD
            ({(azureCredit.consumed_pct || 0).toFixed(1)}% consumido)
          </div>
          {!azureCredit.is_exhausted && (
            <span className="text-[11px] bg-blue-100 px-2 py-1 rounded">
              Custos Azure NÃO reduzem o caixa enquanto houver crédito
            </span>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-bold text-gray-600 sticky left-0 bg-gray-50 min-w-[300px]">
                Conta
              </th>
              {periods.map(p => (
                <th key={p.key} className="px-2 py-2.5 text-right text-xs font-bold text-gray-600 whitespace-nowrap min-w-[90px]">
                  {p.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aggregatedAccounts.map(acc => (
              <AccountRow key={acc.code} account={acc} months={periods.map(p => p.key)} viewMode={viewMode} currency={currency} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-gray-400 text-right">
        {L.footer}
      </div>
    </div>
  );
};

export default FinancialCashflowDetailedTab;
